/**
 * Box Truck Load Offers — a driver texts their ZIP; how often does that become a bid?
 *
 * The funnel is the page: the check-in, the loads that went out, the one they picked, the price they named,
 * the bid we wrote. Read it as a sequence and the drop between two steps is the thing to fix — a desk whose
 * drivers are offered loads and never pick one has a different problem from a desk whose drivers are never
 * offered anything.
 *
 * WHY is the other half, and it is why these rows exist at all. Two kinds of "why" share one word and are
 * kept apart on purpose: the engine REFUSED listings (too far, too old, demands a lift gate this truck
 * lacks), and the DRIVER declined a whole list (too far, wrong direction, pay too low, not empty yet). The
 * first is ours to fix, the second is the market telling us something.
 *
 * Backend: GET /v1/gluon/box-truck-offer-analytics (any control-panel operator). Read-only.
 */
import * as React from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import LinearProgress from '@mui/joy/LinearProgress'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Tooltip from '@mui/joy/Tooltip'
import Typography from '@mui/joy/Typography'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import { NOTE_CODE_LABELS } from '@numeo/types-core'
import {
    ANALYTICS_DEFAULT_WINDOW_DAYS,
    ANALYTICS_WINDOW_LABEL,
    OFFER_ANALYTICS_WINDOW_DAYS,
    type BoxTruckOfferAnalytics,
    type BoxTruckOfferBidStats,
    type BoxTruckOfferDayStats,
    type BoxTruckOfferDemandStats,
    type BoxTruckOfferDeskStats,
    type BoxTruckOfferReasonStats,
    type BoxTruckOfferSource,
    type BoxTruckOfferStepStats,
} from '@numeo/types-main-mobile'
import { fetchBoxTruckOfferAnalytics } from './api'

/**
 * From the funnel's OWN set, so the selector offers exactly what the server accepts — and that set stops at
 * 30 days, which is narrower than the other analytics pages on purpose. The constant carries the measurement
 * that decided it; the short version is that these rows are per search, so a longer window is a page that
 * eventually times out rather than one that merely takes longer.
 */
const WINDOWS = OFFER_ANALYTICS_WINDOW_DAYS.map((value) => ({ value, label: ANALYTICS_WINDOW_LABEL[value] }))

/**
 * The surface the page is read for. `all` is the default and is the dangerous one: the two surfaces do not
 * write the same steps — a Telegram press is the pick and the price at once, so it writes a bid and no `picked`
 * — which is why a page of both can show bids with nobody picking anything. Picking a surface answers it.
 */
const SOURCES: { value: BoxTruckOfferSource | 'all'; label: string }[] = [
    { value: 'all', label: 'All surfaces' },
    { value: 'sms', label: 'SMS' },
    { value: 'telegram', label: 'Telegram' },
]

/**
 * The funnel, in the order a conversation happens — not in the order the server ranked them.
 *
 * The server sorts by volume because that is what a reader scans for; a FUNNEL has to be read in sequence
 * or the drop between two steps says nothing. A step not listed here is still shown, after these, under its
 * own name: the session owns this vocabulary and may add to it without a release here.
 */
const FUNNEL: { step: string; label: string; hint: string }[] = [
    { step: 'located', label: 'Checked in', hint: 'a ZIP we could place' },
    { step: 'offered', label: 'Loads offered', hint: 'listings sent, not messages' },
    { step: 'picked', label: 'Picked one', hint: 'replied with a number — SMS only, a Telegram press is pick and price in one' },
    { step: 'bid_written', label: 'Bid written', hint: 'priced, and on the board' },
]

/** Plain English for the steps that are not the funnel — where a conversation stopped, and why. */
const STEP_LABELS: Record<string, string> = {
    located: 'checked in',
    offered: 'offered',
    offered_none: 'nothing fit',
    not_empty: 'not empty yet',
    picked: 'picked',
    answered_demand: 'answered about their truck',
    declined: 'declined the list',
    decline_reason: 'said why',
    bid_written: 'bid written',
    bid_gone: 'load gone',
    asked_again: 'asked for their ZIP again',
    unreadable: 'we could not read it',
    stopped: 'texted STOP',
    started: 'texted START',
    refused: 'listings the engine refused',
}

/** Plain English for a reason, whichever side it came from. */
const REASON_LABELS: Record<string, string> = {
    TOO_FAR: 'too far',
    TOO_OLD: 'too old',
    TOO_HEAVY: 'too heavy',
    UNREACHABLE: 'cannot reach the pickup in time',
    DEMANDS_WHAT_IT_LACKS: 'demands kit this truck lacks',
    NO_HAUL: 'no haul miles',
    WRONG_DIRECTION: 'wrong direction',
    PAY_TOO_LOW: 'pay too low',
    NOT_EMPTY_YET: 'not empty yet',
}

const stepLabel = (step: string): string => STEP_LABELS[step] ?? step.replace(/_/g, ' ')
const reasonLabel = (reason: string): string => REASON_LABELS[reason] ?? reason.replace(/_/g, ' ').toLowerCase()

const events = (steps: BoxTruckOfferStepStats[], step: string): number => steps.find((s) => s.step === step)?.events ?? 0

/** Bids per check-in, as a whole percent. No check-ins is 0, not NaN — the page prints it. */
const pct = (part: number, whole: number): number => (whole > 0 ? Math.round((part / whole) * 100) : 0)

const rateTone = (rate: number): 'success' | 'warning' | 'danger' => (rate >= 30 ? 'success' : rate >= 10 ? 'warning' : 'danger')

const usd = (value: number | null): string => (value === null ? '—' : `$${value.toLocaleString()}`)

/** How many of the bids a dispatcher then emailed a broker about, as a whole percent. `null` when the board could not be read. */
const answeredPct = (bids: BoxTruckOfferBidStats): number | null => (bids.answered === null ? null : pct(bids.answered, bids.bids))

const answeredText = (bids: BoxTruckOfferBidStats): string =>
    bids.answered === null ? '—' : `${bids.answered.toLocaleString()} / ${bids.bids.toLocaleString()}`

const demandLabel = (code: string): string => (NOTE_CODE_LABELS as Record<string, string>)[code] ?? code.replace(/_/g, ' ').toLowerCase()

/** `SMS 12 · Telegram 416` — a step's total told by surface, so a page of both is still readable. */
const bySourceText = (steps: BoxTruckOfferStepStats[], step: string): string => {
    const found = steps.find((s) => s.step === step)
    return found ? `SMS ${found.bySource.sms.toLocaleString()} · Telegram ${found.bySource.telegram.toLocaleString()}` : ''
}

const fmtDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')

/** The engine's refusals and the drivers' own reasons, told apart by the step that produced them. */
const refusals = (reasons: BoxTruckOfferReasonStats[]) => reasons.filter((r) => r.step === 'refused')
const declines = (reasons: BoxTruckOfferReasonStats[]) => reasons.filter((r) => r.step === 'decline_reason')

const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md', minWidth: 150, flex: 1 }}>
        <Typography level="body-xs" textColor="text.tertiary">
            {label}
        </Typography>
        <Typography level="h3" sx={{ mt: 0.5 }}>
            {value}
        </Typography>
        {hint && (
            <Typography level="body-xs" textColor="text.tertiary" sx={{ whiteSpace: 'pre-line' }}>
                {hint}
            </Typography>
        )}
    </Sheet>
)

const ReasonChips = ({ reasons, limit }: { reasons: BoxTruckOfferReasonStats[]; limit?: number }) => {
    const shown = limit ? reasons.slice(0, limit) : reasons
    if (shown.length === 0)
        return (
            <Typography level="body-xs" textColor="text.tertiary">
                —
            </Typography>
        )
    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {shown.map((reason) => (
                <Chip key={`${reason.step}|${reason.reason}`} size="sm" variant="soft" color="neutral">
                    {reasonLabel(reason.reason)} · {reason.events.toLocaleString()}
                </Chip>
            ))}
        </Box>
    )
}

/** What drivers said about their truck when a load demanded something their record could not answer. */
const DemandChips = ({ demands }: { demands: BoxTruckOfferDemandStats[] }) => {
    if (demands.length === 0)
        return (
            <Typography level="body-xs" textColor="text.tertiary">
                —
            </Typography>
        )
    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {demands.map((demand) => (
                <Chip key={demand.code} size="sm" variant="soft" color="neutral">
                    {demandLabel(demand.code)} · yes {demand.yes.toLocaleString()} / no {demand.no.toLocaleString()}
                </Chip>
            ))}
        </Box>
    )
}

/** One day's figures, in the funnel's own order. */
const dayEvents = (day: BoxTruckOfferDayStats, step: string): number => day.steps.find((s) => s.step === step)?.events ?? 0

/** A step list of the shape `events()` reads, from a day or a driver row. */
const asSteps = (steps: { step: string; events: number }[]): BoxTruckOfferStepStats[] =>
    steps.map((s) => ({ step: s.step, events: s.events, bySource: { sms: 0, telegram: 0 } }))

/**
 * The window, day by day.
 *
 * A total says how much; this says whether it is growing, and which day a change landed on. Days are UTC —
 * the clock the rows were written with, so a desk comparing this against its own evening knows which it is
 * reading.
 */
const DayTable = ({ days }: { days: BoxTruckOfferDayStats[] }) => {
    if (days.length === 0)
        return (
            <Typography level="body-xs" textColor="text.tertiary">
                —
            </Typography>
        )
    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto', mb: 3 }}>
            <Table size="sm" hoverRow>
                <thead>
                    <tr>
                        <th style={{ width: 120 }}>Day (UTC)</th>
                        <th style={{ width: 100 }}>Checked in</th>
                        <th style={{ width: 90 }}>Offered</th>
                        <th style={{ width: 80 }}>Picked</th>
                        <th style={{ width: 70 }}>Bids</th>
                        <th style={{ width: 110 }}>Check-in to bid</th>
                        <th>Everything else</th>
                    </tr>
                </thead>
                <tbody>
                    {days.map((day) => {
                        const rest = day.steps.filter((s) => !FUNNEL.some((f) => f.step === s.step))
                        return (
                            <tr key={day.day}>
                                <td>{day.day}</td>
                                <td>{dayEvents(day, 'located').toLocaleString()}</td>
                                <td>{dayEvents(day, 'offered').toLocaleString()}</td>
                                <td>{dayEvents(day, 'picked').toLocaleString()}</td>
                                <td>{dayEvents(day, 'bid_written').toLocaleString()}</td>
                                <td>{pct(dayEvents(day, 'bid_written'), dayEvents(day, 'located'))}%</td>
                                <td>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {rest.map((step) => (
                                            <Chip key={step.step} size="sm" variant="soft" color="neutral">
                                                {stepLabel(step.step)} · {step.events.toLocaleString()}
                                            </Chip>
                                        ))}
                                    </Box>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </Table>
        </Sheet>
    )
}

/**
 * One desk, opened: WHO is in this funnel.
 *
 * The counts above answer "how many"; a desk fixing its own conversion needs "who" — which drivers texted
 * and where they stopped, and which dispatchers answered the bids that came back. The dispatcher half is
 * read from the same query the Dispatchers Bids page uses, so the two can never disagree.
 */
const DeskDetail = ({ desk }: { desk: BoxTruckOfferDeskStats }) => (
    <Box sx={{ p: 2, display: 'flex', gap: 3, flexWrap: 'wrap', bgcolor: 'background.level1' }}>
        <Box sx={{ flex: 2, minWidth: 460 }}>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 0.75 }}>
                Drivers in this funnel — most recent first
            </Typography>
            {desk.driverRows.length === 0 ? (
                <Typography level="body-xs">—</Typography>
            ) : (
                <Table size="sm">
                    <thead>
                        <tr>
                            <th>Driver</th>
                            <th style={{ width: 90 }}>Checked in</th>
                            <th style={{ width: 80 }}>Offered</th>
                            <th style={{ width: 70 }}>Picked</th>
                            <th style={{ width: 60 }}>Bids</th>
                            <th style={{ width: 110 }}>
                                <Tooltip title="Bids a dispatcher then emailed a broker about (at or after the bid).">
                                    <span>Dispatcher answered</span>
                                </Tooltip>
                            </th>
                            <th style={{ width: 150 }}>Last activity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {desk.driverRows.map((driver) => (
                            <tr key={driver.accountId}>
                                <td>
                                    <Typography level="body-sm">{driver.name}</Typography>
                                    {driver.email && (
                                        <Typography level="body-xs" textColor="text.tertiary">
                                            {driver.email}
                                        </Typography>
                                    )}
                                </td>
                                <td>{events(asSteps(driver.steps), 'located').toLocaleString()}</td>
                                <td>{events(asSteps(driver.steps), 'offered').toLocaleString()}</td>
                                <td>{events(asSteps(driver.steps), 'picked').toLocaleString()}</td>
                                <td>{events(asSteps(driver.steps), 'bid_written').toLocaleString()}</td>
                                <td>{driver.answeredBids === null ? '—' : driver.answeredBids.toLocaleString()}</td>
                                <td>
                                    <Typography level="body-xs">{fmtDate(driver.lastAt)}</Typography>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 340 }}>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 0.75 }}>
                Dispatchers who emailed a broker in this window
            </Typography>
            {desk.dispatcherRows.length === 0 ? (
                <Typography level="body-xs">Nobody here has answered a bid in this window.</Typography>
            ) : (
                <Table size="sm">
                    <thead>
                        <tr>
                            <th>Dispatcher</th>
                            <th style={{ width: 70 }}>Emails</th>
                            <th style={{ width: 60 }}>Loads</th>
                            <th style={{ width: 140 }}>Last email</th>
                        </tr>
                    </thead>
                    <tbody>
                        {desk.dispatcherRows.map((dispatcher) => (
                            <tr key={dispatcher.dispatcherId}>
                                <td>
                                    <Typography level="body-sm">{dispatcher.email ?? dispatcher.dispatcherId}</Typography>
                                </td>
                                <td>{dispatcher.emails.toLocaleString()}</td>
                                <td>{dispatcher.loads.toLocaleString()}</td>
                                <td>
                                    <Typography level="body-xs">{fmtDate(dispatcher.lastEmailAt)}</Typography>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 2, mb: 0.75 }}>
                What drivers said about their truck
            </Typography>
            <DemandChips demands={desk.demands} />

            <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 2, mb: 0.75 }}>
                This desk, day by day
            </Typography>
            <DayTable days={desk.days} />
        </Box>
    </Box>
)

export default function BoxTruckOfferAnalyticsPage() {
    const [days, setDays] = React.useState<number>(ANALYTICS_DEFAULT_WINDOW_DAYS)
    /** `null` is both surfaces. */
    const [source, setSource] = React.useState<BoxTruckOfferSource | null>(null)
    /** Which desk is open. One at a time: the drill-down is wide, and two of them read as one table. */
    const [openDesk, setOpenDesk] = React.useState<string | null>(null)
    const [data, setData] = React.useState<BoxTruckOfferAnalytics | null>(null)
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState('')

    React.useEffect(() => {
        let cancelled = false
        setLoading(true)
        setError('')
        // Cleared with the window and the surface, like the ZIP page: keeping the old rows while a new one fails
        // leaves the selector saying one thing above the numbers of another.
        setData(null)
        fetchBoxTruckOfferAnalytics(days, source)
            .then((analytics) => {
                if (!cancelled) setData(analytics)
            })
            .catch((err: unknown) => {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [days, source])

    const totals = data?.totals
    /** Steps the funnel above does not name, so a step the session adds still appears somewhere. */
    const otherSteps = (totals?.steps ?? []).filter((s) => !FUNNEL.some((f) => f.step === s.step) && s.step !== 'refused' && s.step !== 'decline_reason')

    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <LocalShippingIcon />
                <Typography level="h2">Box Truck Load Offers</Typography>
            </Box>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 2 }}>
                A driver texts their ZIP, the bot offers the loads that fit their truck, and they reply with a number and a price. Read the drop between two
                steps — and the reasons, which say whether it is the market or us.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                <Select value={days} onChange={(_, v) => setDays(v ?? ANALYTICS_DEFAULT_WINDOW_DAYS)} size="sm" sx={{ width: 180 }}>
                    {WINDOWS.map((w) => (
                        <Option key={w.value} value={w.value}>
                            {w.label}
                        </Option>
                    ))}
                </Select>
                <Select value={source ?? 'all'} onChange={(_, v) => setSource(v === 'sms' || v === 'telegram' ? v : null)} size="sm" sx={{ width: 160 }}>
                    {SOURCES.map((option) => (
                        <Option key={option.value} value={option.value}>
                            {option.label}
                        </Option>
                    ))}
                </Select>
            </Box>

            {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && data && data.desks.length === 0 && (
                <Alert color="neutral">
                    No load conversations in this window. A row is written for every step, including the ones that stop — so an empty page means no driver has
                    checked in, not that the bot is failing.
                </Alert>
            )}

            {!loading && totals && data && data.desks.length > 0 && (
                <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        {FUNNEL.map((stage) => (
                            <Stat
                                key={stage.step}
                                label={stage.label}
                                value={events(totals.steps, stage.step).toLocaleString()}
                                hint={source === null ? `${stage.hint}\n${bySourceText(totals.steps, stage.step)}` : stage.hint}
                            />
                        ))}
                        <Stat
                            label="Check-in to bid"
                            value={`${pct(events(totals.steps, 'bid_written'), events(totals.steps, 'located'))}%`}
                            hint={`${totals.drivers.toLocaleString()} drivers · ${totals.desks} ${totals.desks === 1 ? 'desk' : 'desks'}`}
                        />
                        <Stat
                            label="Dispatcher answered"
                            value={answeredPct(totals.bids) === null ? '—' : `${answeredPct(totals.bids)}%`}
                            hint={
                                totals.bids.answered === null
                                    ? 'the board could not be read'
                                    : `${answeredText(totals.bids)} bids got a broker email · ${totals.bids.matched} found on the board${
                                          data.bidsTruncated ? '\nnewest bids only — the window has more than one read keeps' : ''
                                      }`
                            }
                        />
                        <Stat
                            label="Bid price"
                            value={usd(totals.bids.avgPriceUsd)}
                            hint={totals.bids.minPriceUsd === null ? 'average' : `average · ${usd(totals.bids.minPriceUsd)}–${usd(totals.bids.maxPriceUsd)}`}
                        />
                    </Box>

                    {/* The two kinds of why, side by side and never summed. */}
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                        <Box sx={{ flex: 1, minWidth: 320 }}>
                            <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 0.75 }}>
                                Listings the engine kept out — ours to fix
                            </Typography>
                            <ReasonChips reasons={refusals(totals.reasons)} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 320 }}>
                            <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 0.75 }}>
                                What drivers said was wrong with a list — the market talking
                            </Typography>
                            <ReasonChips reasons={declines(totals.reasons)} />
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 0.75 }}>
                            What drivers said about their truck when a load asked
                        </Typography>
                        <DemandChips demands={totals.demands} />
                    </Box>

                    {otherSteps.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 3 }}>
                            <Typography level="body-xs" textColor="text.tertiary">
                                Everything else that happened:
                            </Typography>
                            {otherSteps.map((step) => (
                                <Chip key={step.step} size="sm" variant="soft" color="neutral">
                                    {stepLabel(step.step)} · {step.events.toLocaleString()}
                                </Chip>
                            ))}
                        </Box>
                    )}

                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 0.75 }}>
                        Day by day — the shape of the window, not just its total
                    </Typography>
                    <DayTable days={totals.days} />

                    <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                        <Table size="sm" stickyHeader hoverRow>
                            <thead>
                                <tr>
                                    <th style={{ width: '18%' }}>Desk</th>
                                    <th style={{ width: 90 }}>Drivers</th>
                                    <th style={{ width: 100 }}>Checked in</th>
                                    <th style={{ width: 100 }}>Offered</th>
                                    <th style={{ width: 90 }}>Picked</th>
                                    <th style={{ width: 90 }}>Bids</th>
                                    <th style={{ width: 170 }}>Check-in to bid</th>
                                    <th style={{ width: 120 }}>
                                        <Tooltip title="Bids a dispatcher then emailed a broker about, out of the bids written.">
                                            <span>Dispatcher answered</span>
                                        </Tooltip>
                                    </th>
                                    <th>Why listings were kept out</th>
                                    <th style={{ width: 170 }}>Last activity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.desks.map((desk) => {
                                    const rate = pct(events(desk.steps, 'bid_written'), events(desk.steps, 'located'))
                                    const open = openDesk === desk.projectName
                                    return (
                                        <React.Fragment key={desk.projectName}>
                                            <tr
                                                // The row IS the control: a desk reading a funnel clicks the desk.
                                                onClick={() => setOpenDesk(open ? null : desk.projectName)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td>
                                                    {open ? '▾' : '▸'} {desk.projectName}
                                                </td>
                                                <td>{desk.drivers.toLocaleString()}</td>
                                                <td>{events(desk.steps, 'located').toLocaleString()}</td>
                                                <td>
                                                    <Tooltip title="Listings sent, not messages — one message carries three.">
                                                        <Typography level="body-sm">{events(desk.steps, 'offered').toLocaleString()}</Typography>
                                                    </Tooltip>
                                                </td>
                                                <td>{events(desk.steps, 'picked').toLocaleString()}</td>
                                                <td>{events(desk.steps, 'bid_written').toLocaleString()}</td>
                                                <td>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LinearProgress
                                                            determinate
                                                            value={Math.min(rate, 100)}
                                                            color={rateTone(rate)}
                                                            sx={{ flex: 1, minWidth: 60 }}
                                                        />
                                                        <Typography level="body-xs">{rate}%</Typography>
                                                    </Box>
                                                </td>
                                                <td>{answeredText(desk.bids)}</td>
                                                <td>
                                                    {/* The three that explain a thin list. The tail is noise at this width; the
                                                    whole of it is above, where the rarest refusal is still visible. */}
                                                    <ReasonChips reasons={refusals(desk.reasons)} limit={3} />
                                                </td>
                                                <td>
                                                    <Typography level="body-xs">{fmtDate(desk.lastAt)}</Typography>
                                                </td>
                                            </tr>
                                            {open && (
                                                <tr>
                                                    <td colSpan={10} style={{ padding: 0 }}>
                                                        <DeskDetail desk={desk} />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                            </tbody>
                        </Table>
                    </Sheet>
                </>
            )}
        </Box>
    )
}
